import { animate, style, transition, trigger } from '@angular/animations';
import { Component, ViewEncapsulation, OnInit, OnDestroy, ChangeDetectorRef, ElementRef,
	QueryList, ViewChildren } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AbstractControl, FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule,
	Validators } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { NgIf, NgFor, NgClass, DecimalPipe, TitleCasePipe, KeyValuePipe } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import moment from 'moment';
import { catchError, forkJoin, map, Observable, of, Subscription, tap } from 'rxjs';

import { WizardStepperService } from '../../../services/wizard-stepper.service';
import { WizardStep } from '../../../models/app-wizard-state';
import { FormOffer, FormProduct } from '../../../models/form-offer';
import { AppWizardService } from '../../../services/app-wizard.service';
import { AppOfferService } from '../../../services/app-offer.service';
import { WizardPageContent } from '../wizard-page-content';
import { PERIOD_TYPES, OfferProduct, PRODUCT_TYPE_F, TemplateDescription, PRODUCT_TYPE_NF, VariableField,
	TemplateVariableString,	TemplateVariable, TemplateVariableArray, ProductFamily, ProductDimension,
	ProductShortDescription, FORM_SHEET_CODE, VariableFieldUpdated, FIRST_ROW_FIELDS, FIELD_SHIPTO_PARTY,
	FIELD_TEMPLATE_NUMBER, FIELD_STEEL_GRADE, EXCLUDE_FROM_THIRD_FOR_SIMULATION,
	FIELD_END_USER } from '../../../models/offer';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { InputNumberDirective } from '../../../../shared/directives/input-number.directive';
import { UNDERSCORE } from '../../../../shared/utils/constant';
import { CodeValue as CodeValueType } from '../../../../shared/models/code-value';
import { TemplateVariableValues, ProductConfiguration, CharacteristicValueSimpleObject,
	FieldValueObject, CharacteristicValue } from '../../../../shared/models/product';
import { TranslationService } from '../../../../shared/services/translation.service';
import { ConfigurationService } from '../../../../shared/services/configuration.api.service';
import { fieldTransitions } from '../../../../shared/animations/field-transitions';
import { SortOptionsPipe } from '../../../../shared/pipes/sorting.pipe';
import {
	NAMESPACE_RTPC_FIELD,
	ProductCharacteristicsTranslationService
} from '../../../../shared/services/product-characteristics-translation.services';
import { TRANSLATION_TYPE_SIDE } from '../../../../shared/models/data-with-translations';

@Component({
	selector: 'copo-offer-wizard-stepcart',
	templateUrl: './step-cart.component.html',
	styleUrl: './step-cart.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	animations: [
		trigger('deleteItemTrigger', [
			transition(':leave', [
				animate(750, style({
					opacity: 0,
					height: 0,
				}))
			])
		]), fieldTransitions
	],
	imports: [
		NgIf,
		NgClass,
		DecimalPipe,
		KeyValuePipe,
		FormsModule,
		ReactiveFormsModule,
		NgFor,
		LoadingSpinnerComponent,
		TranslateModule,
		MatIconModule,
		MatFormFieldModule,
		MatMenuModule,
		MatInputModule,
		MatSelectModule,
		MatOptionModule,
		MatButtonModule,
		MatDividerModule,
		MatExpansionModule,
		InputNumberDirective,
		SortOptionsPipe,
		TitleCasePipe,
		MatTooltipModule
	]
})
export class StepCartComponent extends WizardPageContent implements OnInit, OnDestroy {
	@ViewChildren('rows') rows!: QueryList<ElementRef<HTMLElement>>;
	readonly UNDERSCORE = UNDERSCORE;
	readonly form: FormOffer;

	isLoading = true;
	isError = false;
	isExpanded = new Map<string, boolean[]>();
	productFamilies: ProductFamily[] = [];
	soldTo = '';
	shipTo = '';
	endUser = '';
	selectedPeriodDateDisplay = '';
	selectedPeriodDisplay = '';
	selectedPeriodTypeDisplay = '';
	variableconfig = { codeProp: 'code', valueProp: 'value' };
	templatesInLoading = new Map<number, string[]>();
	private readonly simulationSubscriptionsMap = new Map<string, Subscription>();
	private readonly isVariableFieldUpdatedMap = new Map<string, VariableFieldUpdated>();
	private variableDataSub?: Subscription;
	private readonly VIEW_STEP_NUM = 1;

	constructor(
		private readonly appWizardService: AppWizardService,
		private readonly stepperService: WizardStepperService,
		private readonly translationService: TranslationService,
		private readonly configurationService: ConfigurationService,
		private readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService,
		// private readonly offerService: OfferService,
		private readonly appOfferService: AppOfferService,
		public changeDetectorRef: ChangeDetectorRef
	) {
		super(WizardStep.CART, appWizardService.getState());
		this.form = this.appWizardService.getStateOfferForm();
	}

	get getProductFamiliesHavingItemInCart() {
		return this.productFamilies.filter(
			productFamily => productFamily.code && this.getProductsCountBySubCategory(productFamily.code) > 0
		);
	}

	productResultControl(produtFamily: ProductFamily) {
		const formProducts = <FormArray> this.form.get('products');
		return <FormGroup[]> formProducts.controls.filter(
			product => product.get('category')?.value === produtFamily.category
				&& product.get('subCategory')?.value === produtFamily.subCategory
				&& product.get('form')?.value === produtFamily.form
		);
	}

	ngOnInit() {
		this.stepperService.onStepChanged.emit({ step: this.VIEW_STEP_NUM });
		const selSoldToId = <string> this.form.getFormSoldTo().value;
		if (this.appWizardService.soldToList) {
			const selectedSoldTo = this.appWizardService.soldToList.find(v => v.businessId === selSoldToId);
			if (selectedSoldTo) {
				this.soldTo = `${ selectedSoldTo.name } / ${ selectedSoldTo.city }`;
			}
		}

		const selShipToId = <string> this.form.getFormShipTo().value;
		if (this.appWizardService.shipToList) {
			const selectedShipTo = this.appWizardService.shipToList.find(v => v.businessId === selShipToId);
			if (selectedShipTo) {
				this.shipTo = `${ selectedShipTo.name } / ${ selectedShipTo.city }`;
			}
		}

		const selEndUserId = <string> this.form.getFormEndUser().value;
		if (this.appWizardService.endUserList) {
			const selectedEndUser = this.appWizardService.endUserList.find(v => v.businessId === selEndUserId);
			if (selectedEndUser) {
				this.endUser = `${ selectedEndUser.name } / ${ selectedEndUser.city }`;
			}
		}

		const productFamilies = this.appWizardService.getState().ui.getProductFamilies(selSoldToId);
		const formProducts = this.form.getFormProducts();
		const products = formProducts.getProducts();
		if (productFamilies && products) {
			this.productFamilies = productFamilies;
			for (const productFamily of this.productFamilies) {
				const productsInCurrentFamily = products.filter(
					product =>
						product.subCategory === productFamily.subCategory
						&& product.form === productFamily.form
				);
				if (productsInCurrentFamily.length > 0) {
					this.appWizardService.productsGroupedByFamily
						.set(productFamily.code, productsInCurrentFamily.length);
					const expandStatus: boolean[] = [];
					for (let i = 0; i < productsInCurrentFamily.length; i++) {
						expandStatus[i] = true;
					}
					this.isExpanded.set(productFamily.code, expandStatus);
				}
				for (const product of productsInCurrentFamily) {
					this.fetchData(product, product.uniqueRef);
				}
			}
		}
		this.processPeriodInfo();
		this.isLoading = false;
	}

	ngOnDestroy() {
		if (this.variableDataSub) {
			this.variableDataSub.unsubscribe();
		}
		for (const [, subscription] of this.simulationSubscriptionsMap.entries()) {
			subscription.unsubscribe();
		}
	}

	checkError() {
		const formProducts = this.form.getFormProducts();
		if (formProducts.length === 0) {
			return true;
		}
		let indexToScroll = -1;
		let i = 0;
		for (const formProduct of formProducts.getFormProducts()) {
			if (formProduct.invalid) {
				formProduct.markAllAsTouched();
				formProduct.updateValueAndValidity();
				if (indexToScroll === -1) {
					indexToScroll = i;
				}
			}
			i++;
		}
		if (indexToScroll !== -1) {
			const rowItems = this.rows.toArray();
			if (rowItems[indexToScroll]) {
				rowItems[indexToScroll].nativeElement.scrollIntoView();
			}
			return true;
		}
		return false;
	}

	checkErrorForDraft() {
		return false;
	}

	getVariableFieldUpdatedState(uniqueRef: string, variableName: string, multiInputIndex?: number) {
		const variableFieldUpdateState = this.isVariableFieldUpdatedMap.get(uniqueRef);
		if (variableFieldUpdateState) {
			if (multiInputIndex !== undefined && Array.isArray(variableFieldUpdateState)
				&& variableFieldUpdateState[multiInputIndex]) {
				return true;
			} else if (variableFieldUpdateState.has(variableName)) {
				return variableFieldUpdateState.get(variableName);
			}
		}
		return false;
	}

	getPreviousFamiliesItemCount(currentFamilyKey: string) {
		return this.appWizardService.getPreviousFamiliesItemCount(currentFamilyKey);
	}

	expandPanel(key: string, index: number) {
		const expandStatus = this.isExpanded.get(key);
		if (expandStatus) {
			expandStatus[index] = !expandStatus[index];
		}
	}

	getExpandPanelStatus(key: string, index: number) {
		const expandStatus = this.isExpanded.get(key);
		if (expandStatus) {
			return expandStatus[index];
		}
		return true;
	}

	getProductsCountBySubCategory(key: string) {
		if (this.appWizardService.productsGroupedByFamily.has(key)) {
			return this.appWizardService.productsGroupedByFamily.get(key) ?? 0;
		}
		return 0;
	}

	fetchData(offerItem: OfferProduct, uniqueRef?: string) {
		if (offerItem.templateNumber && offerItem.productType !== PRODUCT_TYPE_F) {
			const templateAndDetail = this.appWizardService.getState().ui.getTemplateNumberDetails(
				offerItem.templateNumber
			);
			if (!templateAndDetail) {
				return;
			}
			const indexListInTemplate = this.templatesInLoading.get(offerItem.templateNumber);
			if (indexListInTemplate) {
				indexListInTemplate.push(uniqueRef || '');
				return;
			}
			this.templatesInLoading.set(offerItem.templateNumber, [uniqueRef || '']);
			this.variableDataSub = this.loadAllVariableValues(templateAndDetail)
				.pipe(map(valuesLoaded => valuesLoaded ? templateAndDetail : undefined))
				.subscribe({
					next: templateAndDetailRes => {
						if (templateAndDetailRes && offerItem.templateNumber) {
							this.appWizardService.populateValuesPossiblePerField(offerItem.templateNumber);
							const indexListInTemplateLcl = this.templatesInLoading.get(offerItem.templateNumber);
							if (indexListInTemplateLcl !== undefined) {
								for (const uniqueRefToProcess of indexListInTemplateLcl) {
									const formProduct = this.form.getFormProducts().getFormProduct(uniqueRefToProcess);
									if (formProduct) {
										this.prepareDataAndSimulateA2Cproduct(formProduct, uniqueRefToProcess);
									}
								}
								this.appWizardService.populateVariablesForThirdRow(offerItem.templateNumber);
								this.appWizardService.populateVariablesForOptionalTolerance(
									offerItem.templateNumber
								);
								this.templatesInLoading.delete(offerItem.templateNumber);
							}
						}
					},
					error: (err: unknown) => {
						this.handleError('unexpected error when loading product edition card', err);
					}
				});
		}
	}

	processPeriodInfo() {
		let startMonthValue = <string> this.form.getFormPeriod().getFormPeriodStartMonth().value;
		let endMonthValue = <string> this.form.getFormPeriod().getFormPeriodEndMonth().value;

		const currentDate = new Date();
		if (startMonthValue) {
			const [startYear, startMonth] = startMonthValue.split('-M').map(Number);
			const startDate = new Date(startYear, startMonth - 1, 1);
			if (startDate < currentDate) {
				this.form.getFormPeriod().getFormPeriodStartMonth().setValue('');
				startMonthValue = '';
			}
		}

		if (endMonthValue) {
			const [endYear, endMonth] = endMonthValue.split('-M').map(Number);
			const endDate = new Date(endYear, endMonth - 1, 1);
			if (endDate < currentDate) {
				this.form.getFormPeriod().getFormPeriodEndMonth().setValue('');
				endMonthValue = '';
			}
		}

		if (!this.form.getFormPeriod().getFormPeriodStartMonth().value
			|| !this.form.getFormPeriod().getFormPeriodEndMonth().value) {
			this.selectedPeriodDateDisplay = this.translationService.getTR('global.none');
		}

		if (startMonthValue && endMonthValue) {
			const [startYear, startMonth] = startMonthValue.split('-M').map(Number);
			const [endYear, endMonth] = endMonthValue.split('-M').map(Number);
			const monthDifference = (endYear - startYear) * 12 + (endMonth - startMonth);

			const selectedPeriodType =
					startYear === endYear && startMonth === endMonth ? 'Monthly'
						: monthDifference === 2 && [1, 4, 7, 10].includes(startMonth) ? 'Quarterly'
							: monthDifference === 5 && [1, 7].includes(startMonth) ? 'Semesterly'
								: startMonth === 1 && endMonth === 12 && startYear === endYear ? 'Annually'
									: 'Custom Period';
			const periodType = PERIOD_TYPES.find(period => period.value === selectedPeriodType);
			if (periodType) {
				this.selectedPeriodTypeDisplay = this.translationService.getTR(periodType.label);
			}
			switch (selectedPeriodType) {
			case 'Monthly':
				this.selectedPeriodDisplay = `${ moment().month(startMonth - 1).format('MMMM') } (${ startYear })`;
				break;

			case 'Quarterly':
				this.selectedPeriodDisplay = `${ this.translationService.getTR('eoffer-Quarter') }
				 ${ Math.floor((startMonth - 1) / 3) + 1 } (${ startYear })`;
				break;

			case 'Semesterly':
				this.selectedPeriodDisplay = `${ this.translationService.getTR('eoffer-Semester') }
				 ${ Math.floor((startMonth - 1) / 6) + 1 } (${ startYear })`;
				break;

			case 'Annually':
				this.selectedPeriodDisplay = `${ startYear }`;
				break;

			default:
				this.selectedPeriodDisplay = '';
				break;
			}
		} else if (startMonthValue || endMonthValue) {
			const selectedPeriodType = 'Custom Period';
			const periodType = PERIOD_TYPES.find(period => period.value === selectedPeriodType);
			if (periodType) {
				this.selectedPeriodTypeDisplay = this.translationService.getTR(periodType.label);
			}
		}
		this.updateSelectedPeriodDisplay(startMonthValue, endMonthValue);
	}

	isVariable(templateNumber: number | undefined, variableName: string): boolean {
		if (templateNumber) {
			const valuesPossiblePerField = this.getValuesPossiblePerField(templateNumber);
			return valuesPossiblePerField.some(v => v.keys === variableName);
		}
		return false;
	}

	getFormControl(control: AbstractControl | null) {
		return <FormControl> control;
	}

	getVariableFormGroupControl(uniqueRef: string, controlName: string) {
		const formProduct = this.form.getFormProducts().getFormProduct(uniqueRef);
		return <FormControl> formProduct?.get('variableFormGroup')?.get(controlName);
	}

	getProductIndexByUniqueRef(uniqueRef: string) {
		return this.form.getFormProducts().getFormProductIndex(uniqueRef) ?? null;
	}

	getSteelGradeDescription(uniqueRef: string) {
		const formProduct = this.form.getFormProducts().getFormProduct(uniqueRef);
		return formProduct?.getProduct().steelGradeDescription || ' ';
	}

	getPossibleValues(variableName: string, templateNumber?: number): CodeValueType[] {
		if (templateNumber) {
			const valuesPossiblePerField = this.getValuesPossiblePerField(templateNumber);
			return valuesPossiblePerField.filter(v => v.keys === variableName).flatMap(v => v.values);
		}
		return [];
	}

	isFullyConfigured(productType: string | undefined) {
		return PRODUCT_TYPE_F === productType;
	}

	getFieldLabel(fieldLabel: string) {
		return this.productCharacteristicsTranslationService
			.translate(`${ NAMESPACE_RTPC_FIELD }${ fieldLabel }`);
	}

	compareWithFn(field1: string, field2: string) {
		return field1 === field2;
	}

	getVariablesForThirdRow(templateNumber?: number) {
		if (templateNumber) {
			return this.appWizardService.getVariablesForThirdRow(templateNumber);
		}
		return [];
	}

	getVariableFieldValue(uniqueRef: string, field: string) {
		const formProduct = this.form.getFormProducts().getFormProduct(uniqueRef);
		if (formProduct) {
			const variableFormControl = <FormControl> formProduct.get('variableFormGroup')?.get(field);
			return <string>variableFormControl.value;
		}
		return undefined;
	}

	resetVariableFieldValue(uniqueRef: string, field: string) {
		const formProduct = this.form.getFormProducts().getFormProduct(uniqueRef);
		if (formProduct) {
			const variableFormControl = <FormControl> formProduct.get('variableFormGroup')?.get(field);
			variableFormControl.setValue('');
			variableFormControl.markAsUntouched();
			this.updateBeforeSimulationCall(formProduct);
			this.prepareDataAndSimulateA2Cproduct(formProduct, uniqueRef);
		}
	}

	getVariableFormArray(uniqueRef: string, field: string) {
		const formProduct = this.form.getFormProducts().getFormProduct(uniqueRef);
		const variableFormGroup = <FormGroup> formProduct?.get('variableFormGroup');
		return (<FormArray> variableFormGroup.get(field)).controls;
	}

	getVariableFormArrayValueByIndex(uniqueRef: string, index: number, field: string) {
		const formProduct = this.form.getFormProducts().getFormProduct(uniqueRef);
		const variableFormGroup = <FormGroup> formProduct?.get('variableFormGroup');
		const variableFieldArray = <FormArray> variableFormGroup.get(field);
		return <string> variableFieldArray.controls[index].value;
	}

	checkIfConfigValueInVariableList(field: string, configurationNumber?: string, values?: CodeValueType[]) {
		const configData = this.getConfigData(String(configurationNumber));
		if (configData?.[field]) {
			// eslint-disable-next-line @typescript-eslint/no-base-to-string
			const value = this.removeTranslationPrefix(String(configData[field]));
			return !values?.some(v => v.code === value);
		}
		return false;
	}

	getConfigValue(field: string, configurationNumber?: string) {
		const configData = this.getConfigData(String(configurationNumber));
		if (configData?.[field]) {
			// eslint-disable-next-line @typescript-eslint/no-base-to-string
			return this.removeTranslationPrefix(String(configData[field]));
		}
		return '';
	}

	getTranslationForCodeByIndex(field: string, configurationNumber?: string) {
		const configData = this.getConfigData(String(configurationNumber));
		if (configData?.[field]) {
			// eslint-disable-next-line @typescript-eslint/no-base-to-string
			return this.productCharacteristicsTranslationService.translate(String(configData[field]));
		}
		return '';
	}

	getConfigData(configurationNumber?: string) {
		if (configurationNumber && this.appWizardService.getState().ui.hasConfigurationData(configurationNumber)) {
			return this.appWizardService.getState().ui.getConfigurationData(configurationNumber);
		}
		return null;
	}

	/**
	 * Retrieves the configuration value for a multi-item field.
	 * ex: "labelContent": [ - contentParentFieldName
	 * {
	 * "content": "" - contentChildFieldName
	 * }
	 * ]
	 * @param contentChildFieldName - The name of the child field.
	 * @param uniqueRef - unique identifiable ref of item.
	 * @param index - The index of the item within the multi-item field.
	 * @param contentParentFieldName - The name of the parent field.
	 * @returns The configuration value for the specified field.
	 */
	getConfigValueMultiItems(
		contentChildFieldName: string,
		uniqueRef: string,
		index: number,
		contentParentFieldName: string
	) {
		const configData = this.getConfigDataMultiItems(uniqueRef, contentParentFieldName);
		if (configData) {
			const arrayObject = <CharacteristicValueSimpleObject[]>configData;
			const value = arrayObject[index] ? arrayObject[index][contentChildFieldName] : '';
			return this.removeTranslationPrefix(String(value));
		}
		return '';
	}

	checkIfConfigValueInVariableListMultiItems(
		contentChildFieldName: string,
		uniqueRef: string,
		index: number,
		contentParentFieldName: string,
		values?: CodeValueType[]
	) {
		const configData = this.getConfigDataMultiItems(uniqueRef, contentParentFieldName);
		if (configData) {
			const arrayObject = <CharacteristicValueSimpleObject[]>configData;
			let value = arrayObject[index] ? arrayObject[index][contentChildFieldName] : undefined;
			if (value) {
				value = this.removeTranslationPrefix(String(value));
				return !values?.some(v => v.code === value);
			}
		}
		return false;
	}

	getTranslationForCodeByIndexMultiItems(
		contentChildFieldName: string,
		uniqueRef: string,
		index: number,
		contentParentFieldName: string
	) {
		const configData = this.getConfigDataMultiItems(uniqueRef, contentParentFieldName);
		if (configData) {
			const arrayObject = <CharacteristicValueSimpleObject[]>configData;
			const value = arrayObject[index] ? arrayObject[index][contentChildFieldName] : '';
			return this.productCharacteristicsTranslationService.translate(String(value));
		}
		return '';
	}

	getConfigDataMultiItems(uniqueRef: string, contentParentFieldName: string) {
		const formProduct = this.form.getFormProducts().getFormProduct(uniqueRef);
		if (formProduct) {
			const configurationControl = <FormControl>formProduct.get('configurationNumber');
			const configurationNumber = String(configurationControl.value);
			if (configurationNumber && this.appWizardService.getState().ui.hasConfigurationData(configurationNumber)) {
				const configData = this.appWizardService.getState().ui.getConfigurationData(configurationNumber);
				if (configData?.[contentParentFieldName]) {
					return configData[contentParentFieldName];
				}
			}
		}
		return undefined;
	}

	resetVariableFormArrayValueByIndex(uniqueRef: string, index: number, field: string) {
		const formProduct = this.form.getFormProducts().getFormProduct(uniqueRef);
		if (formProduct) {
			const variableFormGroup = <FormGroup> formProduct.get('variableFormGroup');
			const variableFieldArray = <FormArray> variableFormGroup.get(field);
			variableFieldArray.controls[index].setValue('');
			variableFieldArray.controls[index].markAsUntouched();
		}
	}

	addFieldToVariableFormArray(
		uniqueRef: string,
		maxItemsAllowed: number,
		field: string,
		isMandatory: boolean,
		isHidden: boolean
	) {
		const formProduct = this.form.getFormProducts().getFormProduct(uniqueRef);
		if (formProduct) {
			const variableFormGroup = <FormGroup> formProduct.get('variableFormGroup');
			const formArray = <FormArray> variableFormGroup.get(field);
			if (formArray.length < maxItemsAllowed) {
				if (isMandatory && !isHidden) {
					formArray.push(new FormControl('', Validators.required));
				} else {
					formArray.push(new FormControl(''));
				}
			}
		}
	}

	removeFieldFromVariableFormArray(uniqueRef: string, removeIndex: number, field: string) {
		const formProduct = this.form.getFormProducts().getFormProduct(uniqueRef);
		if (formProduct) {
			const variableFormGroup = <FormGroup> formProduct.get('variableFormGroup');
			const formArray = <FormArray> variableFormGroup.get(field);
			formArray.removeAt(removeIndex);
		}
	}

	getOptionalToleranceFields(templateNumber?: number) {
		if (templateNumber) {
			return this.appWizardService.getOptionalToleranceFields(templateNumber);
		}
		return new Map<string, VariableField[]>();
	}

	isSheet(formValue: string | undefined) {
		return FORM_SHEET_CODE === formValue;
	}

	getSimulationStatus(isSimulationCompleted?: string, errorInSimulation?: string) {
		let response = 'svg-fill-yellow';
		if (isSimulationCompleted === 'true') {
			response = 'svg-fill-green';
		} else if (errorInSimulation) {
			response = 'svg-fill-red';
		}
		return response;
	}

	removeProduct(uniqueRef: string, key: string, index: number) {
		const formProducts = this.form.getFormProducts();
		formProducts.removeProductByUniqueRef(uniqueRef);
		const productCount = this.appWizardService.productsGroupedByFamily.get(key);
		if (productCount) {
			this.appWizardService.productsGroupedByFamily.set(key, productCount - 1);
		}
		const expandStatus = this.isExpanded.get(key);
		if (expandStatus) {
			expandStatus.splice(index, 1);
		}
		this.isVariableFieldUpdatedMap.delete(uniqueRef);
		this.appWizardService.getState().ui.removeSimulationData(uniqueRef);
	}

	disableDuplicateMenu() {
		return this.appWizardService.getProductCount() >= 100;
	}

	duplicateProduct(uniqueRef: string, key: string) {
		if (this.appWizardService.getProductCount() < 100) {
			const formProducts = this.form.getFormProducts();
			const formProduct = formProducts.getFormProduct(uniqueRef);
			if (formProduct) {
				const steelGradeControl = <FormControl>formProduct.get('steelGrade');
				const thicknessControl = <FormControl>formProduct.get('thicknessValue');
				const widthControl = <FormControl>formProduct.get('widthValue');
				const lengthControl = <FormControl>formProduct.get('lengthValue');
				const shipToPartyControl = <FormControl>formProduct.get('shipToParty');
				const configurationControl = <FormControl> formProduct.get('configurationNumber');
				const a2citemNumberControl = <FormControl> formProduct.get('a2citemNumber');
				const inquiryNumberControl = <FormControl> formProduct.get('inquiryNumber');
				const simulationStatusControl = <FormControl> formProduct.get('isSimulationCompleted');
				const configErrMsgControl = <FormControl> formProduct.get('configErrMsg');
				const loaderControl = <FormControl> formProduct.get('loader');
				const categoryControl = <FormControl> formProduct.get('category');
				const subCategoryControl = <FormControl> formProduct.get('subCategory');
				const formControl = <FormControl> formProduct.get('form');
				const endUser = <FormControl> formProduct.get('endUser');
				const { productType } = formProduct.getProduct();
				const { templateNumber } = formProduct.getProduct();
				const { steelGradeDescription } = formProduct.getProduct();
				const newVariableFormGroup = new FormGroup({});
				if (templateNumber !== undefined) {
					const variableFormGroup = <FormGroup> formProduct.get('variableFormGroup');
					for (const variableKey of Object.keys(variableFormGroup.controls)) {
						const variableControl = variableFormGroup.get(variableKey);
						if (variableControl instanceof FormControl) {
							if (variableControl.hasValidator(Validators.required)) {
								newVariableFormGroup.addControl(
									variableKey,
									new FormControl(variableControl.value, Validators.required)
								);
							} else {
								newVariableFormGroup.addControl(variableKey, new FormControl(variableControl.value));
							}
						} else if (variableControl instanceof FormArray) {
							const formArray = new FormArray<FormControl>([]);
							for (const control of variableControl.controls) {
								if (control.hasValidator(Validators.required)) {
									formArray.push(new FormControl(control.value, Validators.required));
								} else {
									formArray.push(new FormControl(control.value));
								}
							}
							newVariableFormGroup.addControl(variableKey, formArray);
						}
					}
				}
				const productFamily: ProductFamily = {
					category: '',
					categoryDescription: '',
					subCategory: '',
					subCategoryDescription: '',
					form: '',
					formDescription: '',
					code: '',
					customerTemplate: [{}]
				};
				const productDimension: ProductDimension = {
					thickness: { value: 0, unit: '' },
					width: { value: 0, unit: '' },
				};
				const description: ProductShortDescription = {
					family: productFamily,
					steelGrade: '',
					dimension: productDimension,
				};
				const productUniqueRef = Array.from(crypto.getRandomValues(new Uint8Array(16)))
					.map(b => b.toString(16).padStart(2, '0'))
					.join('');
				const offerProduct: OfferProduct = {
					a2citemNumber: <string>a2citemNumberControl.value,
					inquiryNumber: <string>inquiryNumberControl.value,
					configurationNumber: <number>configurationControl.value,
					steelGrade: <string>steelGradeControl.value,
					thicknessValue: <number>thicknessControl.value,
					widthValue: <number>widthControl.value,
					lengthValue: <number>lengthControl.value,
					shipToParty: <string>shipToPartyControl.value,
					isSimulationCompleted: <string>simulationStatusControl.value,
					configErrMsg: <string>configErrMsgControl.value,
					loader: <string>loaderControl.value,
					templateNumber,
					productType,
					category: <string>categoryControl.value,
					form: <string>formControl.value,
					subCategory: <string>subCategoryControl.value,
					steelGradeDescription,
					description,
					offerStatus: 'DRAFT',
					feasibilityStatus: 'YES',
					uniqueRef: productUniqueRef,
					endUser: <string>endUser.value
				};
				if (this.isSheet(offerProduct.form)) {
					const lengthValueControl = <FormControl> formProduct.get('lengthValue');
					offerProduct.lengthValue = <number>lengthValueControl.value;
				}
				offerProduct.variableFormGroup = newVariableFormGroup;
				this.form.getFormProducts().addProducts([offerProduct]);
				const expandStatus = this.isExpanded.get(key);
				const productCount = this.appWizardService.productsGroupedByFamily.get(key);
				if (productCount && expandStatus) {
					this.appWizardService.productsGroupedByFamily.set(key, productCount + 1);
					expandStatus.push(true);
				}
				this.isVariableFieldUpdatedMap.set(productUniqueRef, new Map<string, boolean>());
				if (offerProduct.configurationNumber
					&& this.appWizardService.getState().ui
						.hasConfigurationData(String(offerProduct.configurationNumber))) {
					const configData = this.appWizardService.getState().ui
						.getConfigurationData(String(offerProduct.configurationNumber));
					if (configData !== undefined) {
						this.appWizardService.getState().ui.updateSimulationData(productUniqueRef, configData);
					}
				}
			}
		}
	}

	/**
	 * NonFullyCongifured and configData available - resets to its configValues.
	 * NonFullyCongifured and configData not available - resets all editable fields to empty.
	 * FullyConfigured - reset all editable fields to empty.
	 * @param uniqueRef - unique identifiable value in product.
	 */
	resetProduct(uniqueRef: string) {
		const formProducts = this.form.getFormProducts();
		const formProduct = formProducts.getFormProduct(uniqueRef);
		if (formProduct) {
			const configurationControl = <FormControl> formProduct.get('configurationNumber');
			const configurationNumber = String(configurationControl.value);
			const templateNumberControl = <FormControl> formProduct.get('templateNumber');
			const templateNumber = Number(templateNumberControl.value);
			let configData: ProductConfiguration | undefined;
			if (configurationNumber && this.appWizardService.getState().ui.hasConfigurationData(configurationNumber)) {
				configData = this.appWizardService.getState().ui.getConfigurationData(configurationNumber);
			}
			let templateDetails: TemplateDescription | undefined;
			if (templateNumber) {
				templateDetails = this.appWizardService.getState().ui.getTemplateNumberDetails(
					templateNumber
				);
			}

			formProduct.product.quantity = undefined;
			formProduct.getFormQuantity().setValue('');

			if (formProduct.product.productType !== PRODUCT_TYPE_F) {
				if (configData && templateDetails && formProduct.product.productType !== PRODUCT_TYPE_NF) {
					// resetting to config values
					formProduct.product.steelGrade = configData.steelGradeCode;
					if (configData.steelGrade) {
						formProduct.product.steelGradeDescription = this.productCharacteristicsTranslationService
							.translate(configData.steelGrade);
					}
					formProduct.product.thicknessValue = configData.thicknessValue;
					formProduct.product.widthValue = configData.widthValue;
					formProduct.product.lengthValue = configData.lengthValue;
					formProduct.getFormSteelGrade().setValue(configData.steelGradeCode);
					formProduct.getFormThickness().setValue(configData.thicknessValue);
					formProduct.getFormWidth().setValue(configData.widthValue);
					formProduct.getFormLength().setValue(configData.lengthValue);

					this.resetProductVariables(formProduct, configData, templateDetails); // third row variables reset
					this.updateBeforeSimulationCall(formProduct);
					this.prepareDataAndSimulateA2Cproduct(formProduct, uniqueRef);
				} else {
					// resetting to undefined
					formProduct.product.steelGrade = undefined;
					formProduct.product.steelGradeDescription = undefined;
					formProduct.product.thicknessValue = undefined;
					formProduct.product.widthValue = undefined;
					formProduct.product.lengthValue = undefined;
					formProduct.product.isSimulationCompleted = '';
					formProduct.product.configErrMsg = '';

					formProduct.getFormSteelGrade().setValue(undefined);
					formProduct.getFormThickness().setValue(undefined);
					formProduct.getFormWidth().setValue(undefined);
					formProduct.getFormLength().setValue(undefined);
					formProduct.getSimulationCompleted().setValue('');
					formProduct.getConfigErrMsg().setValue('');

					if (configData && templateDetails && formProduct.product.productType === PRODUCT_TYPE_NF) {
						// third row variables reset
						this.resetProductVariables(formProduct, configData, templateDetails);
					} else {
						for (const variableKey of Object.keys(formProduct.getVariableFormGroup().controls)) {
							const control = formProduct.getVariableFormGroup().get(variableKey);
							if (control) {
								this.emptyProductVariableData(control);
							}
						}
					}
				}
			}
			formProduct.setErrors(null);
			formProduct.markAsUntouched();
		}
	}

	simulationCallOnValueChange(uniqueRef: string, formGroup: FormGroup) {
		const formProduct = <FormProduct>formGroup;
		this.updateBeforeSimulationCall(formProduct);
		this.prepareDataAndSimulateA2Cproduct(formProduct, uniqueRef);
	}

	private updateBeforeSimulationCall(formProduct: FormProduct) {
		formProduct.getSimulationCompleted().setValue('');
		formProduct.getConfigErrMsg().setValue('');
		formProduct.getLoader().setValue('false');
		formProduct.product.isSimulationCompleted = '';
		formProduct.product.configErrMsg = '';
		formProduct.product.loader = 'false';
	}

	private prepareDataAndSimulateA2Cproduct(formProduct: FormProduct, uniqueRef: string) {
		const templateNumber = <number> formProduct.get('templateNumber')?.value;
		if (!templateNumber) {
			return;
		}
		let simulationSubscription = this.simulationSubscriptionsMap.get(uniqueRef);
		if (simulationSubscription !== undefined) {
			simulationSubscription.unsubscribe();
		}
		const valuesPossiblePerField = this.getValuesPossiblePerField(templateNumber);
		const variableValuesExcludingFirstRow = this.filterVariablesExcludingFirstRow(valuesPossiblePerField);
		if (this.validateFieldsForSimulation(formProduct)) {
			let productConfigObj = <ProductConfiguration>{};
			formProduct.setErrors(null);
			formProduct.markAsUntouched();
			const simulationCompletedFlagControl = <FormControl> formProduct.get('isSimulationCompleted');
			const configErrMsgControl = <FormControl>formProduct.get('configErrMsg');
			if (!simulationCompletedFlagControl.value && configErrMsgControl.value === '') {
				const templateAndDetail = this.appWizardService.getState().ui.getTemplateNumberDetails(
					templateNumber
				);
				productConfigObj = this.setProperty(productConfigObj, FIELD_TEMPLATE_NUMBER, templateNumber);
				const selectedProduct = <FormProduct>formProduct.value;
				for (const [fieldKey, value] of Object.entries(selectedProduct)) {
					const templateFields = Object.getOwnPropertyNames(templateAndDetail?.properties);
					if ((templateFields.includes(fieldKey) || templateAndDetail?.listOfVariables.includes(fieldKey))
						&& value !== null && value !== '' && value !== undefined) {
						let fieldValue;
						switch (typeof value) {
						case 'string':
							fieldValue = String(value);
							break;
						case 'number':
							fieldValue = Number(value);
							break;
						default:
							break;
						}
						if (fieldValue && fieldValue !== '') {
							if (fieldKey === FIELD_SHIPTO_PARTY || fieldKey === FIELD_END_USER) {
								fieldValue = String(fieldValue).padStart(10, '0');
							}
							productConfigObj = this.setProperty(productConfigObj, fieldKey, fieldValue);
						}
					}
				}
				for (const variableField of variableValuesExcludingFirstRow) {
					const formGroupcontrol = formProduct.get('variableFormGroup')?.get(variableField.keys);
					const keyField = variableField.keys;
					if (formGroupcontrol) {
						if (formGroupcontrol instanceof FormArray) {
							const arrayValues = [];
							for (const control of formGroupcontrol.controls) {
								if (control.value && control.value !== '') {
									arrayValues.push({ [keyField]: String(control.value) });
								}
							}
							productConfigObj = this
								.setProperty(productConfigObj, variableField.parentVariableName, arrayValues);
						} else if (formGroupcontrol.value && formGroupcontrol.value !== '') {
							const value = variableField.type === 'number'
								? Number(formGroupcontrol.value) : String(formGroupcontrol.value);
							productConfigObj = this.setProperty(productConfigObj, keyField, value);
						}
					}
				}
				simulationSubscription = this.simulateA2Cproduct(productConfigObj, formProduct).subscribe({
					next: simulationResponse => {
						if (simulationResponse !== undefined) {
							const configurationErrors = simulationResponse.errors ? [...simulationResponse.errors] : [];
							const simulationData = simulationResponse.configuration;
							if (simulationData) {
								const simulationShipToValue = simulationData.shipToParty?.replace(/^0+/, '');
								const simulationEndUserValue = simulationData.endUser?.replace(/^0+/, '');
								if (formProduct.getFormShipToParty().value !== simulationShipToValue
									|| formProduct.getFormEndUser().value !== simulationEndUserValue) {
									configurationErrors.push(
										this.translationService.getTR('eoffer.simulation.shipto.enduser.error')
									);
								}
							}
							if (configurationErrors.length === 0) {
								this.updateSimulationStatus(formProduct);
								if (simulationData) {
									this.updateSimulationData(
										formProduct,
										simulationData,
										templateNumber
									);
								}
							} else {
								this.updateSimulationStatus(formProduct, configurationErrors);
							}
						} else {
							this.updateSimulationStatus(
								formProduct,
								undefined,
								this.translationService.getTR('global.unexpected.error.identified')
							);
						}
					},
					error: () => {
						this.updateSimulationStatus(
							formProduct,
							undefined,
							this.translationService.getTR('global.unexpected.error.identified')
						);
					}
				});
				this.simulationSubscriptionsMap.set(uniqueRef, simulationSubscription);
			} else {
				const loaderControl = <FormControl> formProduct.get('loader');
				loaderControl.setValue('false');
			}
		}
	}

	private updateSimulationStatus(formProduct: FormProduct, errors?: string[], error?: string) {
		const loaderControl = <FormControl> formProduct.get('loader');
		if (loaderControl.value === 'true') {
			const simulationCompletedFlagControl = <FormControl> formProduct.get('isSimulationCompleted');
			const configErrMsgControl = <FormControl>formProduct.get('configErrMsg');
			if (errors && errors.length > 0) {
				configErrMsgControl.setValue(errors);
			} else if (error) {
				configErrMsgControl.setValue(error);
			}
			if (configErrMsgControl.value) {
				simulationCompletedFlagControl.setValue('');
			} else {
				simulationCompletedFlagControl.setValue('true');
			}
			loaderControl.setValue('false');
			this.changeDetectorRef.detectChanges();
		}
	}

	private updateSimulationData(
		formProduct: FormProduct,
		data: ProductConfiguration,
		templateNumber: number
	) {
		const product = formProduct.getProduct();
		// first and second row update
		const valuesPossiblePerField = this.getValuesPossiblePerField(templateNumber);
		const firstRowVariableFields = this.filterVariablesFirstRow(valuesPossiblePerField);
		const { uniqueRef } = formProduct.getProduct();
		if (uniqueRef !== undefined) {
			this.appWizardService.getState().ui.updateSimulationData(uniqueRef, data);
		}
		for (const field of FIRST_ROW_FIELDS) {
			const formGroupcontrol = formProduct.get(field);
			if (formGroupcontrol && data[field]) {
				const control = <FormControl>formGroupcontrol;
				const variableField = firstRowVariableFields.find(i => i.keys === field);
				if (variableField) {
					this.updateSimulationValueInFormControl(
						variableField,
						variableField.type === 'number'
							? Number(data[variableField.keys]) : <string> data[variableField.keys],
						control,
						variableField.type,
						formProduct.getProduct().uniqueRef || ''
					);
				} else {
					// not a variable
					let value;
					if (field === FIELD_STEEL_GRADE) {
						const simulationValue = String(data[field]);
						value = this.removeTranslationPrefix(simulationValue);
						product.steelGradeDescription = this.productCharacteristicsTranslationService
							.translate(String(simulationValue));
					} else {
						value = Number(data[field]);
					}
					if (control.value !== value) {
						control.setValue(value);
						this.variableFieldUpdatedStateChange(formProduct.getProduct().uniqueRef || '', field);
					}
				}
			}
		}

		// variableFormGroup update
		const thirdRowVariableFields = this.appWizardService.getVariablesForThirdRow(templateNumber);
		for (const variableField of thirdRowVariableFields) {
			const formGroupcontrol = formProduct.get('variableFormGroup')?.get(variableField.keys);
			if (formGroupcontrol && (data[variableField.keys] || data[variableField.parentVariableName])) {
				if (variableField.type === 'array') {
					let arrayObject: CharacteristicValueSimpleObject[] = [];
					if (data[variableField.parentVariableName]) {
						arrayObject = <CharacteristicValueSimpleObject[]> data[variableField.parentVariableName];
					}
					const formArray = <FormArray> formGroupcontrol;
					let i = 0;
					for (const obj of arrayObject) {
						if (formArray.controls[i].value !== obj[variableField.keys]) {
							this.updateSimulationValueInFormControl(
								variableField,
								String(obj[variableField.keys]),
								<FormControl>formArray.controls[i],
								'string',
								formProduct.getProduct().uniqueRef || '',
								i
							);
						}
						i++;
					}
				} else {
					this.updateSimulationValueInFormControl(
						variableField,
						variableField.type === 'number'
							? Number(data[variableField.keys]) : <string> data[variableField.keys],
						<FormControl>formGroupcontrol,
						variableField.type,
						formProduct.getProduct().uniqueRef || ''
					);
				}
			}
		}

		// tolerance row update
		const optionalToleranceGroup = this.appWizardService.getOptionalToleranceFields(templateNumber);
		for (const [, variableFields] of optionalToleranceGroup.entries()) {
			for (const variableField of variableFields) {
				const formGroupControl = formProduct.get('variableFormGroup')?.get(variableField.keys);

				if (formGroupControl && data[variableField.keys]) {
					this.updateSimulationValueInFormControl(
						variableField,
						variableField.type === 'number'
							? Number(data[variableField.keys]) : <string> data[variableField.keys],
						<FormControl>formGroupControl,
						variableField.type,
						formProduct.getProduct().uniqueRef || ''
					);
				}
			}
		}
		setTimeout(() => {
			this.variableFieldUpdatedStateReset(formProduct.getProduct().uniqueRef || '');
		}, 3000);
	}

	private variableFieldUpdatedStateReset(uniqueRef: string) {
		const variableFieldUpdateState = this.isVariableFieldUpdatedMap.get(uniqueRef);
		if (variableFieldUpdateState) {
			for (const [key, value] of variableFieldUpdateState.entries()) {
				if (Array.isArray(value)) {
					for (let i = 0; i < value.length; i++) {
						value[i] = false;
					}
				} else {
					variableFieldUpdateState.set(key, false);
				}
			}
		}
	}

	private filterVariablesFirstRow(fieldKeyValues: VariableField[]) {
		return fieldKeyValues
			.filter(fieldKeyValue =>
				FIRST_ROW_FIELDS.includes(fieldKeyValue.keys))
			.sort((a, b) => a.keys.localeCompare(b.keys));
	}

	private updateSimulationValueInFormControl(
		variableField: VariableField,
		simulationValue: number | string,
		control: FormControl,
		type: string,
		uniqueRef: string,
		multiInputIndex?: number
	) {
		if (type === 'number' && control.value !== simulationValue) {
			control.setValue(simulationValue);
			this.variableFieldUpdatedStateChange(uniqueRef, variableField.keys, multiInputIndex);
		} else if (type === 'string') {
			const value = this.removeTranslationPrefix(String(simulationValue));
			if (control.value !== value) {
				control.setValue(value);
				this.variableFieldUpdatedStateChange(uniqueRef, variableField.keys, multiInputIndex);
				if (!variableField.freeType && !variableField.values.some(v => v.code === value)) {
					// include in list
					const displayValue = this.productCharacteristicsTranslationService
						.translate(String(simulationValue));
					const newValue: CodeValueType = { code: value, value: displayValue };
					variableField.values.push(newValue);
				}
			}
		}
	}

	private variableFieldUpdatedStateChange(uniqueRef: string, variableName: string, multiInputIndex?: number) {
		let variableFieldUpdateState = this.isVariableFieldUpdatedMap.get(uniqueRef);
		if (!variableFieldUpdateState) {
			variableFieldUpdateState = new Map<string, boolean>();
			this.isVariableFieldUpdatedMap.set(uniqueRef, variableFieldUpdateState);
		}
		if (multiInputIndex !== undefined) {
			let multiInputFieldUpdated = variableFieldUpdateState.get(variableName) || [];
			if (Array.isArray(multiInputFieldUpdated)) {
				multiInputFieldUpdated[multiInputIndex] = true;
			} else {
				multiInputFieldUpdated = [];
				multiInputFieldUpdated[multiInputIndex] = true;
				variableFieldUpdateState.set(variableName, multiInputFieldUpdated);
			}
		} else {
			variableFieldUpdateState.set(variableName, true);
		}
	}

	private filterVariablesExcludingFirstRow(fieldKeyValues: VariableField[]) {
		return fieldKeyValues
			.filter(fieldKeyValue =>
				!EXCLUDE_FROM_THIRD_FOR_SIMULATION.includes(fieldKeyValue.keys))
			.sort((a, b) => a.keys.localeCompare(b.keys));
	}

	private simulateA2Cproduct(configuration: ProductConfiguration | FieldValueObject, formProduct: FormProduct) {
		const configErrMsgControl = <FormControl> formProduct.get('configErrMsg');
		configErrMsgControl.setValue('');
		const loaderControl = <FormControl> formProduct.get('loader');
		loaderControl.setValue('true');
		return this.configurationService
			.simulateTemplateProduct(
				configuration,
				TRANSLATION_TYPE_SIDE
			)
			.pipe(
				catchError((err: unknown) => {
					console.error('unable to simulate', err);
					return of(undefined);
				})
			);
	}

	private validateFieldsForSimulation(formProduct: FormProduct) {
		const steelGradeControl = <FormControl>formProduct.get('steelGrade');
		const thicknessControl = <FormControl>formProduct.get('thicknessValue');
		const widthControl = <FormControl>formProduct.get('widthValue');
		const lengthControl = <FormControl>formProduct.get('lengthValue');
		const shipToPartyControl = <FormControl>formProduct.get('shipToParty');
		const templateNumber = <number>formProduct.get('templateNumber')?.value;
		return (!this.isVariable(templateNumber, 'steelGrade') || steelGradeControl.valid)
			&& (!this.isVariable(templateNumber, 'thicknessValue') || thicknessControl.valid)
			&& (!this.isVariable(templateNumber, 'widthValue') || widthControl.valid)
			&& (!this.isVariable(templateNumber, 'shipToParty') || shipToPartyControl.valid)
			&& (!this.isSheet(<string>formProduct.get('form')?.value)
				|| !this.isVariable(templateNumber, 'lengthValue') || lengthControl.valid)
			&& this.validateVariableFormGroup(formProduct);
	}

	private validateVariableFormGroup(formProduct: FormProduct) {
		for (const key of Object.keys(formProduct.getVariableFormGroup().controls)) {
			const formGroupControl = formProduct.getVariableFormGroup().get(key);
			if (formGroupControl) {
				if (formGroupControl instanceof FormArray) {
					for (const control of formGroupControl.controls) {
						if (!control.valid) {
							return false;
						}
					}
				} else if (!formGroupControl.valid) {
					return false;
				}
			}
		}
		return true;
	}

	private handleError(message: string, err: unknown) {
		this.templatesInLoading.clear();
		console.error(message, err);
		this.appOfferService.showError();
	}

	private loadAllVariableValues(template: TemplateDescription) {
		const variableValueObservables: Observable<boolean>[] = [];
		for (const variableName of Object.getOwnPropertyNames(template.properties)) {
			const variable = template.properties[variableName];
			this.loadAllVariableValuesByType(template, variableName, variable, variableValueObservables);
		}
		return forkJoin(variableValueObservables).pipe(map(results => results.filter(isOk => !isOk).length === 0));
	}

	private updateSelectedPeriodDisplay(fromMonth: string, toMonth?: string) {
		if (fromMonth && toMonth) {
			this.selectedPeriodDateDisplay = `${ fromMonth } - ${ toMonth }`;
		} else if (fromMonth) {
			this.selectedPeriodDateDisplay = `${ fromMonth } -`;
		} else if (toMonth) {
			this.selectedPeriodDateDisplay = `- ${ toMonth }`;
		} else {
			this.selectedPeriodDateDisplay = this.translationService.getTR('global.none');
		}
	}

	private loadAllVariableValuesByType(
		template: TemplateDescription,
		variableName: string,
		variable: TemplateVariable,
		variableValueObservables: Observable<boolean>[],
		group?: string,
		order?: number
	) {
		const freeType = (<TemplateVariableString> variable).free;
		const templateVariableValues: TemplateVariableValues = {
			type: variable.type,
			values: [],
			freeType,
			maxItems: undefined,
			parentVariableName: variableName,
			mandatory: variable.mandatory,
			hidden: variable.hidden,
			group,
			order
		};
		switch (variable.type) {
		case 'number':
		case 'integer':
		case 'string':
			variableValueObservables.push(this.loadVariableValues(
				template.templateNumber,
				variableName,
				templateVariableValues
			));
			break;
		case 'array':
			{
				const variableArray = <TemplateVariableArray>variable;
				for (const entryVariableName of Object.getOwnPropertyNames(variableArray.properties)) {
					const variableLocal = variableArray.properties[entryVariableName];
					const freeTypeLocal = (<TemplateVariableString> variableLocal).free;
					templateVariableValues.freeType = freeTypeLocal;
					templateVariableValues.maxItems = variableArray.maxItems;
					variableValueObservables.push(this.loadVariableValues(
						template.templateNumber,
						entryVariableName,
						templateVariableValues
					));
				}
			}
			break;
		case 'group':
			this.processGroupVariable(template, variable, variableValueObservables, variableName);
			break;
		}
	}

	private processGroupVariable(
		template: TemplateDescription,
		groupVariable: TemplateVariable,
		variableValueObservables: Observable<boolean>[],
		group: string
	) {
		if (groupVariable.properties) {
			for (const variableName of Object.getOwnPropertyNames(groupVariable.properties)) {
				const variable = groupVariable.properties[variableName];
				this.loadAllVariableValuesByType(
					template,
					variableName,
					variable,
					variableValueObservables,
					group,
					variable.order
				);
			}
		}
	}

	private loadVariableValues(
		templateNumber: number,
		variableName: string,
		templateVariableValues: TemplateVariableValues
	) {
		if (this.appWizardService.checkVariableValuesExistByTemplateNumberAndVariableName(
			templateNumber,
			variableName
		)) {
			return of(true);
		}
		if (templateVariableValues.freeType || templateVariableValues.type === 'number') {
			this.prepareVariableValuesAgainstTemplateNumber(
				templateNumber,
				variableName,
				templateVariableValues
			);
			return of(true);
		}
		return this.configurationService.getVariableValues(templateNumber, variableName)
			.pipe(
				tap(values => {
					values.freeType = templateVariableValues.freeType;
					values.maxItems = templateVariableValues.maxItems;
					values.type = templateVariableValues.type;
					values.parentVariableName = templateVariableValues.parentVariableName;
					values.group = templateVariableValues.group;
					values.order = templateVariableValues.order;
					values.hidden = templateVariableValues.hidden;
					this.prepareVariableValuesAgainstTemplateNumber(
						templateNumber,
						variableName,
						values
					);
				}),
				map(() => true),
				catchError((err: unknown) => {
					console.error(
						`unable to load values for variable '${ variableName }' of template '${ templateNumber }'`,
						err
					);
					return of(false);
				})
			);
	}

	private prepareVariableValuesAgainstTemplateNumber(
		templateNumber: number,
		variableName: string,
		values: TemplateVariableValues
	) {
		let templateVariableValues = this.appWizardService.variableValuesIndexedByTemplateNumber
			.get(templateNumber);
		if (templateVariableValues === undefined) {
			templateVariableValues = new Map<string, TemplateVariableValues>();
			this.appWizardService.variableValuesIndexedByTemplateNumber
				.set(templateNumber, templateVariableValues);
		}
		templateVariableValues.set(variableName, values);
	}

	private getValuesPossiblePerField(templateNumber: number): VariableField[] {
		return this.appWizardService.getValuesPossiblePerField(templateNumber);
	}

	private removeTranslationPrefix(value: string): string {
		return value.slice(Math.max(0, value.lastIndexOf('.') + 1));
	}

	/**
	 * Resets third row product variables to their configuration values.
	 * @param formProduct - The form containing the product variables.
	 * @param configData - The configuration data for the product.
	 * @param templateDetails - The template details for the product.
	 */
	private resetProductVariables(
		formProduct: FormProduct,
		configData: ProductConfiguration,
		templateDetails: TemplateDescription
	) {
		for (const key of Object.keys(formProduct.getVariableFormGroup().controls)) {
			const variable = templateDetails.properties[key];
			const control = formProduct.getVariableFormGroup().get(key);
			if (control) {
				if (formProduct.product.productType !== PRODUCT_TYPE_NF || variable.hidden) {
					if (control instanceof FormArray) {
						this.resetProductVariablesMultiItems(control, configData, templateDetails, key);
					} else if (configData[key] && typeof configData[key] === 'string') {
						control.setValue(this.removeTranslationPrefix(String(configData[key])));
					} else {
						control.setValue(configData[key]);
					}
				} else {
					this.emptyProductVariableData(control);
				}
			}
		}
	}

	/**
	 * Resets third row multi-occurrence fields to their configuration values.
	 * If confidData is not available, clearing formArray and adding one item in it with empty value.
	 * if configData available, clearing formArray and pushing number of configData available.
	 * @param formArray - form array containing the product variables.
	 * @param configData - configuration data for the product.
	 * @param templateDetails - template details - get contentChildFieldName and process equalant value from configData.
	 * @param variableName - name of the variable.
	 */
	private resetProductVariablesMultiItems(
		formArray: FormArray,
		configData: ProductConfiguration,
		templateDetails: TemplateDescription,
		variableName: string
	) {
		formArray.controls = []; // clearing formArray items
		const arrayObject: CharacteristicValueSimpleObject[] =
		/**
		 * multioccurrence array value from configData
		 */
		<CharacteristicValueSimpleObject[]> configData[variableName];
		const variable = templateDetails.properties[variableName];
		const variableArray = <TemplateVariableArray>variable;
		this.appWizardService.prepareVariableFormGroupArray(
			formArray,
			arrayObject,
			variableArray,
			variable.mandatory
		);
	}

	private emptyProductVariableData(control: AbstractControl) {
		if (control instanceof FormArray) {
			let isMandatory = false;
			if (control.controls.length > 0) {
				const [formControl] = control.controls;
				if (formControl.hasValidator(Validators.required)) {
					isMandatory = true;
				}
			}
			control.controls = [];
			if (isMandatory) {
				control.controls.push(new FormControl('', Validators.required));
			} else {
				control.controls.push(new FormControl(''));
			}
		} else {
			control.setValue(undefined);
		}
	}

	private setProperty<T, K extends keyof T>(obj: T, key: K, value: CharacteristicValue) {
		obj[key] = <T[K]>value;
		return obj;
	}
}
